import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';

const articleImagesForCategory: Record<string, string> = {
  'article-cheng91-entrance': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20digital%20platform%20entrance%20gateway%20concept%2C%20warm%20amber%20and%20cream%20gradient%20background%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20abstract%20technology%20atmosphere%2C%20professional%20yet%20approachable%20aesthetic%2C%20high%20quality%20editorial%20photography%20with%20smooth%20textures&width=800&height=480&seq=article-cheng91-entrance&orientation=landscape',
  'article-register-guide': 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20registration%20form%20interface%20design%20concept%2C%20warm%20cream%20and%20soft%20amber%20color%20palette%2C%20modern%20UI%20elements%20with%20rounded%20corners%2C%20professional%20digital%20product%20photography%20style%2C%20soft%20diffused%20lighting%2C%20welcoming%20atmosphere&width=800&height=480&seq=article-register-guide&orientation=landscape',
  'article-member-benefits': 'https://readdy.ai/api/search-image?query=Elegant%20membership%20and%20VIP%20concept%20visual%2C%20warm%20golden%20and%20cream%20tones%2C%20minimalist%20luxury%20aesthetic%2C%20abstract%20geometric%20crown%20shapes%2C%20soft%20gradient%20background%2C%20premium%20branding%20photography%20style%2C%20refined%20atmosphere&width=800&height=480&seq=article-member-benefits&orientation=landscape',
  'article-platform-features': 'https://readdy.ai/api/search-image?query=Modern%20digital%20platform%20dashboard%20interface%20concept%2C%20warm%20amber%20and%20cream%20color%20scheme%2C%20clean%20minimalist%20UI%20design%2C%20soft%20abstract%20shapes%2C%20professional%20technology%20photography%2C%20smooth%20gradients%2C%20organized%20layout&width=800&height=480&seq=article-platform-features&orientation=landscape',
  'article-login-fix': 'https://readdy.ai/api/search-image?query=Minimalist%20login%20screen%20troubleshooting%20concept%2C%20warm%20cream%20and%20soft%20amber%20background%2C%20clean%20interface%20elements%2C%20help%20and%20support%20theme%2C%20professional%20digital%20product%20photography%2C%20soft%20diffused%20lighting%2C%20approachable%20aesthetic&width=800&height=480&seq=article-login-fix&orientation=landscape',
  'article-security-guide': 'https://readdy.ai/api/search-image?query=Digital%20security%20and%20protection%20concept%2C%20warm%20amber%20and%20cream%20gradient%20background%2C%20abstract%20shield%20and%20lock%20geometric%20shapes%2C%20minimalist%20design%2C%20professional%20atmosphere%2C%20soft%20lighting%2C%20trustworthy%20aesthetic&width=800&height=480&seq=article-security-guide&orientation=landscape',
  'article-summer-event': 'https://readdy.ai/api/search-image?query=Vibrant%20summer%20celebration%20and%20event%20promotion%20concept%2C%20warm%20golden%20amber%20and%20cream%20tones%2C%20festive%20yet%20minimalist%20aesthetic%2C%20abstract%20geometric%20shapes%2C%20soft%20gradient%20background%2C%20joyful%20atmosphere%2C%20professional%20branding%20photography&width=800&height=480&seq=article-summer-event&orientation=landscape',
  'article-speed-tips': 'https://readdy.ai/api/search-image?query=Fast%20digital%20connection%20and%20speed%20optimization%20concept%2C%20warm%20amber%20and%20cream%20gradient%2C%20abstract%20data%20flow%20geometric%20shapes%2C%20minimalist%20technology%20aesthetic%2C%20soft%20lighting%2C%20dynamic%20yet%20clean%20composition&width=800&height=480&seq=article-speed-tips&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors">返回首页</Link>
        </div>
      </div>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 成91官方`,
    description: category.description,
    url: `https://cheng91.com/category/${category.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          name: article.title,
          url: `https://cheng91.com/article/${article.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <PageSEO
        title={`${category.name} - 成91`}
        description={category.description}
        keywords={`成91,${category.name},成91${category.name},成91教程`}
        canonicalUrl={`/category/${category.slug}`}
      />
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-8 bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category.name },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="lg:w-64 flex-shrink-0">
                <h3 className="text-sm font-medium text-foreground-500 mb-3">内容分类</h3>
                <nav className="space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors whitespace-nowrap ${
                        cat.slug === slug
                          ? 'bg-primary-50 text-primary-600 font-medium'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      <span>{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-500">{cat.articleCount}</span>
                    </Link>
                  ))}
                </nav>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="mb-8">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                    {category.name}
                  </h1>
                  <p className="text-foreground-600">{category.description}</p>
                </div>

                {categoryArticles.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-4">
                      <i className="ri-inbox-line text-2xl text-foreground-500"></i>
                    </div>
                    <p className="text-foreground-600">该分类暂无文章</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {categoryArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="group rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 hover:-translate-y-1 overflow-hidden cursor-pointer flex flex-col"
                      >
                        <div className="relative h-44 w-full overflow-hidden">
                          <img
                            src={articleImagesForCategory[article.imageSeq]}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-5 flex flex-col flex-1">
                          <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
                            <span>{article.date}</span>
                            <span>·</span>
                            <span>{article.readTime} 分钟阅读</span>
                          </div>
                          <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
                            {article.excerpt}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}